import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import '../Customers/Customers.css';
import './Admin.css';

function AuthLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    username: '',
    date: '',
    limit: 100,
  });
  const [availableDates, setAvailableDates] = useState([]);

  useEffect(() => {
    loadAvailableDates();
    loadLogs();
  }, []);

  const loadAvailableDates = async () => {
    try {
      const response = await adminAPI.getLogDates();
      setAvailableDates(response.dates || []);
    } catch (err) {
      console.error('Error loading log dates:', err);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (filters.type !== 'all') params.type = filters.type;
      if (filters.username) params.username = filters.username;
      if (filters.date) params.date = filters.date;
      if (filters.limit) params.limit = filters.limit;

      const response = await adminAPI.getAuthLogs(params);
      setLogs(response.logs || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load authentication logs');
      console.error('Error loading logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    loadLogs();
  };

  const handleResetFilters = () => {
    setFilters({
      type: 'all',
      username: '',
      date: '',
      limit: 100,
    });
    setTimeout(() => loadLogs(), 0);
  };

  const getLevelBadgeClass = (level) => {
    switch (level.toLowerCase()) {
      case 'error':
      case 'critical':
      case 'alert':
      case 'emergency':
        return 'badge-error';
      case 'warning':
        return 'badge-warning';
      case 'info':
      case 'notice':
        return 'badge-info';
      case 'debug':
        return 'badge-debug';
      default:
        return 'badge-default';
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'auth':
        return 'badge-auth';
      case 'device':
        return 'badge-device';
      case 'security':
        return 'badge-security';
      default:
        return 'badge-default';
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-NZ', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (e) {
      return timestamp;
    }
  };

  const renderContext = (context) => {
    if (!context || Object.keys(context).length === 0) {
      return null;
    }

    return (
      <div className="log-context">
        {Object.entries(context).map(([key, value]) => (
          <div key={key} className="log-context-item">
            <span className="log-context-key">{key}:</span>
            <span className="log-context-value">
              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading && logs.length === 0) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main-wrapper">
          <main className="dashboard-content">
            <div className="customer-list-container">
              <div className="loading">Loading authentication logs...</div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="customer-list-container">
            <div className="customer-list-header">
              <h1>Authentication Logs</h1>
              <p className="subtitle">View and monitor authentication, device, and security events</p>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Filters */}
            <div className="filters-container">
              <div className="filters-row">
                <div className="filter-group">
                  <label htmlFor="type-filter">Log Type</label>
                  <select
                    id="type-filter"
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Types</option>
                    <option value="auth">Authentication</option>
                    <option value="device">Device</option>
                    <option value="security">Security</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="username-filter">Username</label>
                  <input
                    id="username-filter"
                    type="text"
                    value={filters.username}
                    onChange={(e) => handleFilterChange('username', e.target.value)}
                    placeholder="Filter by username..."
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="date-filter">Date</label>
                  <select
                    id="date-filter"
                    value={filters.date}
                    onChange={(e) => handleFilterChange('date', e.target.value)}
                    className="filter-select"
                  >
                    <option value="">All Dates</option>
                    {availableDates.map(date => (
                      <option key={date} value={date}>{date}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="limit-filter">Limit</label>
                  <select
                    id="limit-filter"
                    value={filters.limit}
                    onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                    className="filter-select"
                  >
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                    <option value="500">500</option>
                  </select>
                </div>
              </div>

              <div className="filters-actions">
                <button onClick={handleApplyFilters} className="btn btn-primary">
                  Apply Filters
                </button>
                <button onClick={handleResetFilters} className="btn btn-secondary">
                  Reset
                </button>
              </div>
            </div>

            {/* Logs Display */}
            <div className="logs-stats">
              <span>Showing {logs.length} log entries</span>
              {loading && <span className="loading-indicator">Refreshing...</span>}
            </div>

            <div className="logs-container">
              {logs.length === 0 ? (
                <div className="info-box">
                  <p>No logs found matching the current filters.</p>
                </div>
              ) : (
                <div className="logs-list">
                  {logs.map((log, index) => (
                    <div key={index} className="log-entry">
                      <div className="log-header">
                        <div className="log-badges">
                          <span className={`badge ${getTypeBadgeClass(log.type)}`}>
                            {log.type.toUpperCase()}
                          </span>
                          <span className={`badge ${getLevelBadgeClass(log.level)}`}>
                            {log.level.toUpperCase()}
                          </span>
                        </div>
                        <div className="log-timestamp">
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </div>
                      <div className="log-message">
                        {log.message}
                      </div>
                      {renderContext(log.context)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default AuthLogs;
